<script setup lang="ts">
import { computed } from "vue";
import ColorPicker from "../ColorPicker.vue";
import MergeTagInput from "../MergeTagInput.vue";
import { useI18n } from "../../composables/useI18n";
import {
  inputClass,
  inputGroupInputClass,
  inputSuffixClass,
  labelClass,
} from "../../constants/styleConstants";
import type {
  ButtonBlock,
  ButtonBorderSides,
  ButtonClickAction,
} from "@aswin.dev/types";

const props = defineProps<{
  block: ButtonBlock;
  fontFamilies: Array<{ value: string; label: string }>;
}>();

const emit = defineEmits<{
  (e: "update", updates: Partial<ButtonBlock>): void;
}>();

const { t } = useI18n();

const effectiveClickAction = computed(
  (): ButtonClickAction => props.block.clickAction ?? "none",
);

const showLinkUrlFields = computed(() => {
  const a = effectiveClickAction.value;
  return a === "none" || a === "load_page";
});

const urlFieldLabel = computed(() =>
  effectiveClickAction.value === "load_page" ? t.button.pageUrl : t.button.url,
);

const selectActionModel = computed(() =>
  effectiveClickAction.value === "none" ? "" : effectiveClickAction.value,
);

function updateField(field: string, value: unknown): void {
  emit("update", { [field]: value } as Partial<ButtonBlock>);
}

const BORDER_SIDE_IDS: ButtonBorderSides[] = [
  "all",
  "top",
  "right",
  "bottom",
  "left",
];

const borderSideOptions = computed(() =>
  BORDER_SIDE_IDS.map((id) => ({
    id,
    label:
      t.button.borderSidesLabels[id as keyof typeof t.button.borderSidesLabels],
  })),
);

function setClickActionFromSelect(ev: Event): void {
  const value = (ev.target as HTMLSelectElement).value;
  const action = (value === "" ? "none" : value) as ButtonClickAction;
  const patch: Partial<ButtonBlock> = {};
  if (action === "none") {
    patch.clickAction = undefined;
    patch.clickActionAnchorId = undefined;
  } else {
    patch.clickAction = action;
    if (action !== "scroll_anchor") {
      patch.clickActionAnchorId = undefined;
    }
  }
  emit("update", patch);
}
</script>

<template>
  <div class="tpl:mb-3.5">
    <label :class="labelClass">{{ t.button.fontFamily }}</label>
    <select
      :class="inputClass"
      :value="block.fontFamily || ''"
      @change="
        updateField(
          'fontFamily',
          ($event.target as HTMLSelectElement).value || undefined,
        )
      "
    >
      <option value="">{{ t.button.inheritFont }}</option>
      <option
        v-for="font in fontFamilies"
        :key="font.value"
        :value="font.value"
      >
        {{ font.label }}
      </option>
    </select>
  </div>
  <div class="tpl:mb-3.5">
    <label :class="labelClass">{{ t.button.text }}</label>
    <MergeTagInput
      :model-value="block.text"
      type="text"
      @update:model-value="updateField('text', $event)"
    />
  </div>

  <div class="tpl:mb-3.5">
    <label
      :class="[
        labelClass,
        'tpl:!mb-1 tpl:!text-xs tpl:!font-semibold tpl:!uppercase tpl:!tracking-wide',
      ]"
    >
      {{ t.button.actionOnClick }}
    </label>
    <select
      :class="inputClass"
      :value="selectActionModel"
      @change="setClickActionFromSelect"
    >
      <option value="">{{ t.button.clickActionNone }}</option>
      <option value="next_step">{{ t.button.clickActionNextStep }}</option>
      <option value="previous_step">
        {{ t.button.clickActionPreviousStep }}
      </option>
      <option value="load_page">{{ t.button.clickActionLoadPage }}</option>
      <option value="scroll_anchor">
        {{ t.button.clickActionScrollAnchor }}
      </option>
      <option value="close_popup">{{ t.button.clickActionClosePopup }}</option>
    </select>
  </div>

  <div v-if="effectiveClickAction === 'scroll_anchor'" class="tpl:mb-3.5">
    <label :class="labelClass">{{ t.button.anchorId }}</label>
    <input
      type="text"
      :class="inputClass"
      :value="block.clickActionAnchorId ?? ''"
      :placeholder="t.button.anchorIdPlaceholder"
      autocapitalize="off"
      autocomplete="off"
      spellcheck="false"
      @input="
        updateField(
          'clickActionAnchorId',
          ($event.target as HTMLInputElement).value.trim() || undefined,
        )
      "
    />
  </div>

  <div v-if="showLinkUrlFields" class="tpl:mb-3.5">
    <label :class="labelClass">{{ urlFieldLabel }}</label>
    <MergeTagInput
      :model-value="block.url"
      type="url"
      :placeholder="t.button.urlPlaceholder"
      @update:model-value="updateField('url', $event)"
    />
    <label
      v-if="block.url"
      class="tpl:mt-2 tpl:flex tpl:cursor-pointer tpl:items-center tpl:gap-2 tpl:text-[12px] tpl:text-[var(--tpl-text-muted)]"
    >
      <input
        type="checkbox"
        class="tpl:size-3.5 tpl:cursor-pointer tpl:accent-[var(--tpl-primary)]"
        :checked="block.openInNewTab ?? false"
        @change="
          updateField(
            'openInNewTab',
            ($event.target as HTMLInputElement).checked,
          )
        "
      />
      {{ t.button.openInNewTab }}
    </label>
  </div>

  <div class="tpl:flex tpl:flex-col tpl:gap-3">
    <div class="tpl:w-full tpl:min-w-0">
      <label :class="labelClass">{{ t.button.background }}</label>
      <ColorPicker
        :model-value="block.backgroundColor"
        @update:model-value="updateField('backgroundColor', $event)"
      />
    </div>
    <div class="tpl:w-full tpl:min-w-0">
      <label :class="labelClass">{{ t.button.textColor }}</label>
      <ColorPicker
        :model-value="block.textColor"
        @update:model-value="updateField('textColor', $event)"
      />
    </div>
  </div>

  <div class="tpl:mt-3 tpl:flex tpl:flex-col tpl:gap-3">
    <p
      :class="[
        labelClass,
        'tpl:!mb-0 tpl:!text-xs tpl:!font-semibold tpl:!uppercase tpl:!tracking-wide',
      ]"
    >
      {{ t.button.border }}
    </p>
    <div class="tpl:w-full tpl:min-w-0">
      <label :class="labelClass">{{ t.button.borderColor }}</label>
      <ColorPicker
        :model-value="block.borderColor ?? '#000000'"
        @update:model-value="updateField('borderColor', $event)"
      />
    </div>
    <div>
      <label :class="labelClass">{{ t.button.borderWidth }}</label>
      <div class="tpl:flex tpl:items-stretch">
        <input
          type="number"
          :class="inputGroupInputClass"
          :value="block.borderWidth ?? 0"
          min="0"
          max="20"
          @input="
            updateField(
              'borderWidth',
              Math.max(
                0,
                Number(($event.target as HTMLInputElement).value) || 0,
              ),
            )
          "
        />
        <span :class="inputSuffixClass">px</span>
      </div>
    </div>
    <div>
      <label :class="labelClass">{{ t.button.borderSides }}</label>
      <select
        :class="inputClass"
        :value="block.borderSides ?? 'all'"
        @change="
          updateField(
            'borderSides',
            ($event.target as HTMLSelectElement).value as ButtonBorderSides,
          )
        "
      >
        <option v-for="opt in borderSideOptions" :key="opt.id" :value="opt.id">
          {{ opt.label }}
        </option>
      </select>
    </div>
  </div>

  <div class="tpl:mt-3 tpl:flex tpl:flex-col tpl:gap-3">
    <div>
      <label :class="labelClass">{{ t.button.borderRadius }}</label>
      <div class="tpl:flex tpl:items-stretch">
        <input
          type="number"
          :class="inputGroupInputClass"
          :value="block.borderRadius"
          min="0"
          max="50"
          @input="
            updateField(
              'borderRadius',
              Number(($event.target as HTMLInputElement).value),
            )
          "
        />
        <span :class="inputSuffixClass">px</span>
      </div>
    </div>
    <div>
      <label :class="labelClass">{{ t.button.fontSize }}</label>
      <div class="tpl:flex tpl:items-stretch">
        <input
          type="number"
          :class="inputGroupInputClass"
          :value="block.fontSize"
          min="10"
          max="36"
          @input="
            updateField(
              'fontSize',
              Number(($event.target as HTMLInputElement).value),
            )
          "
        />
        <span :class="inputSuffixClass">px</span>
      </div>
    </div>
  </div>
</template>
